import React, { useEffect } from 'react';
import { Prospect } from '../../shared/types';
import { fashionProducts, fashionOrders } from '../../shared/data/ecommerceData';
import { ShoppingBag, Package, Truck, TrendingUp, Search, Plus, DollarSign } from 'lucide-react';

export const EcommerceSystem: React.FC<{ prospect: Prospect }> = ({ prospect }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#111111] font-sans flex flex-col selection:bg-[#111111] selection:text-white">
      
      {/* Top Navbar Store ERP - STRICT NO SHADOWS */}
      <header className="h-20 bg-[#111111] border-b border-zinc-800 px-8 flex items-center justify-between shrink-0">
         <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#047857] text-white flex items-center justify-center font-black">
               <ShoppingBag className="w-5 h-5" />
            </div>
            <div>
               <h1 className="font-heading font-black text-lg text-white tracking-wider uppercase">StoreERP — {prospect.trade_name}</h1>
               <p className="text-[10px] text-zinc-400 font-medium">Gestão de Vendas E-commerce & Grade de Estoque</p>
            </div>
         </div>

         <div className="flex items-center gap-4">
            <div className="relative hidden md:block">
               <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
               <input type="text" placeholder="Buscar pedido por nº ou cliente..." className="pl-9 pr-4 py-2 bg-zinc-900 border border-zinc-700 rounded-xl text-xs text-white outline-none w-72 placeholder:text-zinc-500" />
            </div>
            <button className="bg-[#047857] text-white px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 hover:bg-emerald-700 transition-colors border border-emerald-500">
               <Plus className="w-4 h-4" /> Novo Produto
            </button>
         </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 p-8 overflow-y-auto">
         
         {/* Metrics Summary - STRICT NO SHADOWS */}
         <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl border border-zinc-200">
               <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest block mb-1">Faturamento E-commerce (Mês)</span>
               <span className="text-3xl font-heading font-bold text-[#111111]">R$ 48.920</span>
               <p className="text-xs text-[#047857] font-bold mt-2">↑ 18.4% vs mês anterior</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-zinc-200">
               <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest block mb-1">Pedidos a Despachar</span>
               <span className="text-3xl font-heading font-bold text-amber-600">8</span>
               <p className="text-xs text-amber-600 font-bold mt-2">Prontos para expedição</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-zinc-200">
               <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest block mb-1">Ticket Médio por Compra</span>
               <span className="text-3xl font-heading font-bold text-[#111111]">R$ 642,00</span>
               <p className="text-xs text-zinc-500 font-medium mt-2">2.4 itens por sacola</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-zinc-200">
               <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest block mb-1">Taxa de Conversão da Loja</span>
               <span className="text-3xl font-heading font-bold text-[#047857]">3.4%</span>
               <p className="text-xs text-zinc-500 font-medium mt-2">Benchmarking do setor: 2.1%</p>
            </div>
         </div>

         {/* Tabela de Pedidos - STRICT NO SHADOWS */}
         <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
            <div className="p-6 border-b border-zinc-200 flex justify-between items-center bg-zinc-50">
               <h3 className="font-heading font-bold text-lg text-[#111111]">Esteira de Pedidos Recentes</h3>
               <span className="text-xs font-bold text-zinc-500">Sincronizado via Correios / Melhor Envio</span>
            </div>

            <div className="overflow-x-auto">
               <table className="w-full text-left text-xs">
                  <thead className="bg-zinc-100 border-b border-zinc-200 text-zinc-600 font-bold uppercase tracking-wider">
                     <tr>
                        <th className="p-4">Nº Pedido / Data</th>
                        <th className="p-4">Cliente</th>
                        <th className="p-4">Itens do Pedido</th>
                        <th className="p-4">Total</th>
                        <th className="p-4">Status de Despacho</th>
                        <th className="p-4">Rastreio</th>
                     </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-200">
                     {fashionOrders.map((o) => (
                        <tr key={o.id} className="hover:bg-zinc-50 transition-colors">
                           <td className="p-4 font-mono font-bold text-[#111111]">{o.id}<span className="block text-[10px] text-zinc-400 font-sans font-normal">{o.date}</span></td>
                           <td className="p-4 font-bold text-zinc-800">{o.customerName}</td>
                           <td className="p-4 text-zinc-600">{o.items.join(', ')}</td>
                           <td className="p-4 font-black text-[#047857]">R$ {o.total.toFixed(2)}</td>
                           <td className="p-4">
                              <span className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase border ${o.status === 'Enviado' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-amber-50 text-amber-800 border-amber-200'}`}>
                                 {o.status}
                              </span>
                           </td>
                           <td className="p-4 font-mono text-zinc-500 text-[10px] font-bold">{o.trackingCode}</td>
                        </tr>
                     ))}
                  </tbody>
               </table>
            </div>
         </div>

      </main>

    </div>
  );
};
